import { useCallback, useEffect, useRef } from 'react'

import { useGetReceptionDevice } from '@reception-web/common/api/www'
import * as Sentry from '@sentry/react'
import { Trans } from 'react-i18next'
import { AiOutlineLoading3Quarters } from 'react-icons/ai'
import { useNavigate } from 'react-router-dom'

import { Text } from '../../components'
import { ERROR_TYPE, PATH } from '../../constants'
import { useContactProvider, useDeviceIdProvider } from '../../hooks'
import {
  startAlexConnectCall,
  stopActiveAlexConnectCall,
} from '../../utils/alexConnectWidget'
import {
  resolveAutomatedCommunicationSystem,
  usesAlexAutomatedCommunicationSystem,
} from '../../utils/automatedCommunicationSystem'

function isAbortError(error: unknown) {
  return error instanceof DOMException && error.name === 'AbortError'
}

export function AlexConnectFrame() {
  const navigate = useNavigate()
  const { language, resetContact } = useContactProvider()
  const { deviceId, getRoute } = useDeviceIdProvider()
  const receptionDevice = useGetReceptionDevice(deviceId ?? '')
  const startedRef = useRef(false)
  const handledRef = useRef(false)
  const closedRef = useRef(false)
  const mountedRef = useRef(true)
  const abortControllerRef = useRef<AbortController | null>(null)
  const cleanupTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  )
  const callOwnerTokenRef = useRef({})
  const isReceptionDeviceLoading =
    receptionDevice.data === undefined && receptionDevice.error === undefined

  const navigateToAlexMaintenance = useCallback(
    (reason: string, extra: Record<string, unknown> = {}) => {
      Sentry.captureException(
        new Error('Failed to launch Alex Connect Widget'),
        {
          tags: {
            alert: 'critical',
            feature: 'alex_connect_widget',
          },
          extra: {
            deviceId,
            reason,
            ...extra,
          },
          fingerprint: ['alex-connect-widget', reason],
        }
      )
      navigate(getRoute(PATH.maintenance), {
        state: { error: ERROR_TYPE.FAILED_TO_LAUNCH_ALEX },
      })
    },
    [deviceId, getRoute, navigate]
  )

  useEffect(
    () => {
      const callOwnerToken = callOwnerTokenRef.current
      mountedRef.current = true
      if (cleanupTimerRef.current) {
        clearTimeout(cleanupTimerRef.current)
        cleanupTimerRef.current = undefined
      }

      return () => {
        mountedRef.current = false
        cleanupTimerRef.current = setTimeout(() => {
          abortControllerRef.current?.abort()
          stopActiveAlexConnectCall(callOwnerToken)
          cleanupTimerRef.current = undefined
        }, 0)
      }
    },
    []
  )

  useEffect(() => {
    if (startedRef.current || handledRef.current || isReceptionDeviceLoading) {
      return
    }

    const receptionDeviceData = receptionDevice.data?.data
    const automatedCommunicationSystem = resolveAutomatedCommunicationSystem({
      apiValue: receptionDeviceData?.automated_communication_system,
    })

    if (!automatedCommunicationSystem.ok) {
      handledRef.current = true
      navigateToAlexMaintenance(automatedCommunicationSystem.reason)
      return
    }

    if (
      !usesAlexAutomatedCommunicationSystem(automatedCommunicationSystem.value)
    ) {
      handledRef.current = true
      navigate(getRoute(PATH.dial))
      return
    }

    const propertyId = receptionDeviceData?.property_id
    if (typeof propertyId !== 'string' || propertyId.trim() === '') {
      handledRef.current = true
      navigateToAlexMaintenance('property_id_missing', {
        propertyId,
        automatedCommunicationSystem: automatedCommunicationSystem.value,
      })
      return
    }

    startedRef.current = true
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    startAlexConnectCall({
      hotelId: propertyId,
      language,
      ownerToken: callOwnerTokenRef.current,
      signal: abortController.signal,
      onClose: () => {
        if (!mountedRef.current || closedRef.current) return

        closedRef.current = true
        resetContact()
        navigate(getRoute(PATH.root))
      },
    }).catch((error) => {
      if (abortController.signal.aborted || isAbortError(error)) {
        return
      }
      if (!mountedRef.current) return

      handledRef.current = true
      navigateToAlexMaintenance(
        error instanceof Error && 'reason' in error
          ? String(error.reason)
          : 'alex_widget_unknown_error',
        {
          propertyId,
          hotelId: propertyId,
          automatedCommunicationSystem: automatedCommunicationSystem.value,
        }
      )
    })
  }, [
    getRoute,
    isReceptionDeviceLoading,
    language,
    navigate,
    navigateToAlexMaintenance,
    receptionDevice.data?.data,
    resetContact,
  ])

  return (
    <div className="flex h-screen w-full flex-col items-center justify-center gap-5 bg-off-white p-[60px]">
      <Text size="heading2" className="flex">
        <Trans i18nKey="frames.DialFrame.waiting" tOptions={{ lng: 'ja' }} />
        <br />
        <Trans i18nKey="frames.DialFrame.waiting" tOptions={{ lng: 'en' }} />
      </Text>
      <AiOutlineLoading3Quarters size={40} className="animate-spin" />
    </div>
  )
}
